import {
  initialQueryState,
  QueryResponseContextProps,
  QueryState,
} from '../../../_metronic/helpers'
import { CustomSelect, IOption } from '../Input/CustomSelect'
import styles from './styles/DataTable.module.css'
import { ItemLabel } from './tableTypes'

const PAGE_SIZE_OPTIONS: IOption<number>[] = [10, 25, 50, 100].map((n) => ({
  label: String(n),
  value: n,
}))

type TablePaginationProps = {
  useQueryRequest: () => { updateState: (newState: Partial<QueryState>) => void }
  useQueryResponse: () => QueryResponseContextProps<unknown>
  itemLabel?: ItemLabel
  /** Quantos números de página aparecem ao redor da atual. */
  pageRange?: number
}

/** Janela de páginas ao redor da atual. */
const pageWindow = (current: number, total: number, range: number) => {
  const half = Math.floor(range / 2)
  let start = Math.max(0, current - half)
  const end = Math.min(total - 1, start + range - 1)
  start = Math.max(0, end - range + 1)
  return Array.from({ length: end - start + 1 }, (_, i) => start + i)
}

/**
 * Rodapé do CustomTable: "1–10 de 24 instituições", itens por página
 * e paginação com aria-current. Página é 0-based, como no backend.
 */
export const TablePagination = ({
  useQueryRequest,
  useQueryResponse,
  itemLabel = { singular: 'registro', plural: 'registros' },
  pageRange = 5,
}: TablePaginationProps) => {
  const { response, isLoading } = useQueryResponse()
  const { updateState } = useQueryRequest()

  const page = response?.page ?? initialQueryState.page
  const size = response?.size || initialQueryState.size
  const total = response?.totalElements ?? 0
  const totalPages = Math.max(1, response?.totalPages ?? Math.ceil(total / size))

  if (!total) return null

  const goTo = (target: number) => {
    if (isLoading || target === page || target < 0 || target >= totalPages) return
    updateState({ page: target, size })
  }

  const from = page * size + 1
  const to = Math.min((page + 1) * size, total)
  const isFirst = page === 0
  const isLast = page >= totalPages - 1

  return (
    <div className={styles.footer}>
      <div className={styles.footerInfo}>
        <span className={styles.tabular}>
          <strong className='text-gray-900 fw-semibold'>
            {from}–{to}
          </strong>{' '}
          de {total} {total === 1 ? itemLabel.singular : itemLabel.plural}
        </span>
        <div className={styles.pageSize}>
          <span>Itens por página</span>
          <div className={styles.pageSizeSelect}>
            <CustomSelect
              options={PAGE_SIZE_OPTIONS}
              value={PAGE_SIZE_OPTIONS.find((o) => o.value === size)}
              isClearable={false}
              onChange={(option?: IOption<number>) =>
                option && updateState({ page: 0, size: option.value })
              }
            />
          </div>
        </div>
      </div>
      <nav aria-label='Paginação' className={styles.pages}>
        <button
          type='button'
          className={styles.pageButton}
          onClick={() => goTo(page - 1)}
          disabled={isFirst}
        >
          Anterior
        </button>
        {pageWindow(page, totalPages, pageRange).map((p) =>
          p === page ? (
            <button
              key={p}
              type='button'
              className={styles.pageButtonCurrent}
              aria-current='page'
              aria-label={`Página ${p + 1}`}
            >
              {p + 1}
            </button>
          ) : (
            <button
              key={p}
              type='button'
              className={styles.pageButton}
              aria-label={`Página ${p + 1}`}
              onClick={() => goTo(p)}
            >
              {p + 1}
            </button>
          )
        )}
        <button
          type='button'
          className={styles.pageButton}
          onClick={() => goTo(page + 1)}
          disabled={isLast}
        >
          Próximo
        </button>
      </nav>
    </div>
  )
}
